import { Checkbox, Grid, Box, FormControlLabel, Stack, FormHelperText, } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import TooltipField from 'src/shared/support/TooltipField';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO } from 'date-fns';
import AiButton from 'src/shared/ai/AiButton';
import { CustomField } from './CustomField';
import React, { useContext } from 'react';
import { CardFieldLayoutContext } from 'src/core/ui/ContentCard';
import { CheckboxReview, ReviewTextField, useFieldReview } from './FieldReview';

export const FormField = ({ fieldProps, formikProps, children, sx, }: FormFieldProps) => {
  const label =
    fieldProps.label ??
    (fieldProps.name?.charAt(0).toUpperCase() + fieldProps.name?.slice(1));
  const onChangeFunction = fieldProps?.handleChange ?? formikProps?.handleChange;

  const { t }: { t: any } = useTranslation();

  const resolvedType: ResolvedType =
    fieldProps.type ??
    (fieldProps.date
      ? 'date'
      : fieldProps.checkbox
        ? 'checkbox'
        : fieldProps.select
          ? 'select'
          : fieldProps.multiLine
            ? 'multiline'
            : 'text');

  const inCardLayout = useContext(CardFieldLayoutContext);
  const compact = (fieldProps.md ?? 12) <= 3;
  const useActionRail = inCardLayout && resolvedType !== 'checkbox' && !compact;
  const name = fieldProps.name;
  const { locked } = useFieldReview(name);
  const touched = formikProps?.touched?.[name];
  const error = formikProps?.errors?.[name];
  const hasSubmitted = (formikProps?.submitCount ?? 0) > 0;
  const showError = (touched || hasSubmitted) && error;

  // full status object
  const status = formikProps?.status ?? {};
  const showAllTooltips = Boolean(status.showAllTooltips);
  const tooltipText: React.ReactNode = fieldProps.tooltipText;

  // Hints from formik.status.hints
  const hints = status?.hints ?? {};
  const hintForField = hints?.[name];
  const showHint = !showError && Boolean(hintForField);

  // Field meta (warning, etc) from schema
  const fieldMeta = status?.fieldMeta?.[name] ?? {};
  const metaWarning: string | undefined = fieldMeta.warning;

  const value = formikProps?.values?.[name];
  const isEmpty = value === '' || value === null || typeof value === 'undefined';

  const showWarning =
    !showError &&
    !showHint &&
    Boolean(metaWarning) &&
    (touched || hasSubmitted) &&
    isEmpty;

  const baseHelperText = showError
    ? error
    : showHint
      ? hintForField
      : showWarning
        ? metaWarning
        : '';

  // If info-button mode is on and there is tooltipText, use it as info helper text
  const useTooltipInfo = !baseHelperText && !showError && showAllTooltips && !!tooltipText;

  const helperText = useTooltipInfo ? tooltipText : baseHelperText;

  const isError = Boolean(showError);
  const isInfo = !isError && (showHint || useTooltipInfo);
  const isWarning = !isError && !isInfo && showWarning;

  const actions = <>
    {fieldProps.showTooltipIcon !== false && <TooltipField tooltipText={fieldProps.tooltipText} />}
    {fieldProps.aiField ? <AiButton formikProps={formikProps} fieldName={fieldProps.name} fieldInstructions={fieldProps?.aiInstructions ?? null} /> : null}
    {fieldProps.actions}
  </>;

  return (
    <>
      <Grid item display="flex" md={fieldProps.md ?? 12} xs={fieldProps.xs ?? (inCardLayout ? 12 : 5.8)} sx={inCardLayout ? { minWidth: 0, alignSelf: resolvedType === 'checkbox' ? 'center' : undefined, ...(compact ? { '@container bold-card-fields (max-width: 520px)': { flexBasis: '50%', maxWidth: '50%' } } : {}), } : undefined}>
        <Grid item md={12} xs={12} display={useActionRail ? 'grid' : 'flex'} sx={useActionRail ? { gridTemplateColumns: 'minmax(0, 1fr) 32px', columnGap: 0.75, minWidth: 0 } : undefined} alignItems="center" width="100%">
          {resolvedType === 'date' ? (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker label={label} ampm={false} disabled={Boolean(fieldProps.disabled || locked)} value={formikProps?.values?.[fieldProps.name] && formikProps?.values?.[fieldProps.name] !== '' ? parseISO(formikProps?.values?.[fieldProps.name]) : null} onChange={(value) => { formikProps.setFieldValue(fieldProps.name, value ? new Date(value).toISOString() : null); }} slots={{ textField: ReviewTextField }} slotProps={{ textField: { fullWidth: true, InputLabelProps: { shrink: true }, margin: 'normal', name: fieldProps.name, onBlur: (e: React.FocusEvent<HTMLInputElement>) => { formikProps.setFieldTouched(fieldProps.name, true); formikProps.handleBlur(e); }, error: Boolean(isError), helperText, sx, FormHelperTextProps: isInfo ? { sx: { color: 'info.main' } } : isWarning ? { sx: { color: 'warning.main' } } : undefined, }, }} />
            </LocalizationProvider>
          ) : resolvedType === 'checkbox' ? (
            <CheckboxReview name={name} label={label}>
              <Box display="flex" alignItems="center">
                <FormControlLabel sx={sx} control={<Checkbox disabled={Boolean(fieldProps.disabled || locked)} name={fieldProps.name} checked={toBoolean(formikProps?.values?.[fieldProps.name]) ?? false} value={formikProps?.values?.[fieldProps.name] ?? false} onBlur={formikProps?.handleBlur} onChange={onChangeFunction} />} label={label} />
                <Stack display="flex" spacing={2}>
                  {Boolean(isError) && (
                    <FormHelperText error>{helperText}</FormHelperText>
                  )}
                  {!isError && isInfo && (
                    <FormHelperText sx={{ color: 'info.main' }}>{helperText}</FormHelperText>
                  )}
                  {!isError && !isInfo && isWarning && (
                    <FormHelperText sx={{ color: 'warning.main' }}>{helperText}</FormHelperText>
                  )}
                </Stack>
              </Box>
            </CheckboxReview>
          ) : (
            <CustomField options={{ ...fieldProps, tooltipText: fieldProps.tooltipText, select: fieldProps.select ?? (resolvedType === 'select'), multiLine: fieldProps.multiLine ?? (resolvedType === 'multiline'), }} stateProps={formikProps} sx={fieldProps.sx ?? sx}>{children}</CustomField>
          )}

          {useActionRail ? <Box sx={{ width: 32, alignSelf: 'start', mt: '16px', minHeight: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', '& .MuiIconButton-root': { ml: 0 }, '& > span': { display: 'inline-flex' }, }}>{actions}</Box> : actions}
        </Grid>
      </Grid>
    </>
  );
};

const toBoolean = (value: any) => {
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return Boolean(value);
};

type ResolvedType = 'date' | 'checkbox' | 'select' | 'multiline' | 'text';

interface FormFieldProps {
  fieldProps: any;
  formikProps?: any;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}
